import { motion } from "framer-motion";
import { 
  Planet, 
  Scales, 
  Brain, 
  Certificate, 
  Info,
  Check
} from "@phosphor-icons/react";

// Layer configuration - Using CALM color palette
const layers = [
  { id: "L1", name: "Swiss Ephemeris", desc: "Sub-arcminute astronomical precision", icon: Planet, color: "bg-[var(--blue-fantastic)]", textColor: "text-[var(--blue-fantastic)]", bgColor: "bg-[var(--palladian-light)]", borderColor: "border-[var(--palladian)]" },
  { id: "L2", name: "Vedic Rules", desc: "Classical Jyotish logic engine", icon: Scales, color: "bg-[var(--status-success)]", textColor: "text-[var(--status-success)]", bgColor: "bg-[var(--status-success-bg)]", borderColor: "border-[var(--palladian)]" },
  { id: "L3", name: "AI Analysis", desc: "Contextual interpretation layer", icon: Brain, color: "bg-[var(--truffle)]", textColor: "text-[var(--truffle)]", bgColor: "bg-[var(--oatmeal-light)]", borderColor: "border-[var(--oatmeal)]" },
  { id: "L4", name: "Verified Corpus", desc: "Classical text citations (BPHS, etc.)", icon: Certificate, color: "bg-[var(--burning-flame)]", textColor: "text-[var(--burning-flame)]", bgColor: "bg-[var(--oatmeal-light)]", borderColor: "border-[var(--oatmeal)]" }
];

/**
 * VedicFooter - Standardized footer with 4-Layer Analysis badge and disclaimer
 * Use this at the bottom of all pages and reports
 */
const VedicFooter = ({ 
  showLayers = true, 
  showDisclaimer = true,
  compact = false,
  disclaimerText = null,
  className = ""
}) => {
  if (compact) {
    return (
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className={`mt-6 rounded-xl bg-[var(--bg-muted)] border border-[var(--border-default)] overflow-hidden ${className}`}
      >
        <div className="p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Compact Layer Badges */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600 font-medium">Powered by:</span>
              <div className="flex gap-1">
                {layers.map((layer) => (
                  <div
                    key={layer.id}
                    className={`w-6 h-6 rounded-full ${layer.color} flex items-center justify-center`}
                    title={layer.name}
                  >
                    <span className="text-[9px] font-bold text-white">{layer.id}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Compact Disclaimer */}
            {showDisclaimer && (
              <p className="text-[10px] text-gray-500 text-center sm:text-right">
                {disclaimerText || "Vedic guidance only • Not a substitute for professional advice"}
              </p>
            )}
          </div>
        </div>
        
        {/* Three-Layer Footer Structure - Compact */}
        <div className="border-t border-[var(--border-default)]">
          <div className="px-4 py-2.5 text-center bg-[var(--bg-elevated)]">
            <p className="text-[10px] text-[var(--truffle)] font-medium mb-1">
              AADHYAA — Rooted in Vedic Wisdom. Refined by Modern Science. Guided by Responsible AI.
            </p>
            <p className="text-[9px] text-[var(--text-muted)] flex items-center justify-center gap-1.5">
              <span className="inline-block w-3 h-2 rounded-sm overflow-hidden flex-shrink-0">
                <span className="block h-[3px] bg-[#FF9933]"></span>
                <span className="block h-[2px] bg-white"></span>
                <span className="block h-[3px] bg-[#138808]"></span>
              </span>
              Proudly made in India for the world
              <span className="mx-1">•</span>
              © {new Date().getFullYear()} AADHYAA Technologies Pvt. Ltd.
            </p>
          </div>
        </div>
      </motion.footer>
    );
  }

  return (
    <motion.footer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.3 }}
      className={`mt-8 rounded-2xl bg-[var(--bg-elevated)] border border-[var(--border-default)] overflow-hidden ${className}`}
    >
      {/* Header */}
      <div className="px-5 py-4 bg-[var(--bg-muted)] border-b border-[var(--border-default)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Certificate className="w-5 h-5 text-amber-600" />
            <span className="text-sm font-cinzel font-semibold text-gray-800">Powered by 4-Layer Vedic Architecture</span>
          </div>
          <span className="text-[10px] px-2.5 py-1 rounded-full bg-[var(--status-success-bg)] text-[var(--status-success)] font-semibold border border-[var(--palladian)]">
            Production Ready
          </span>
        </div>
      </div>
      
      {/* Layer Breakdown */}
      {showLayers && (
        <div className="p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {layers.map((layer) => {
              const Icon = layer.icon;
              return (
                <div 
                  key={layer.id} 
                  className={`p-3 rounded-xl bg-white border ${layer.borderColor} hover:shadow-md transition-shadow`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 rounded-lg ${layer.color} flex items-center justify-center`}>
                      <Icon className="w-4 h-4 text-white" weight="fill" />
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-gray-400">{layer.id}</span>
                      <p className="text-xs font-semibold text-gray-800 leading-tight">{layer.name}</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-500 leading-relaxed">{layer.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Disclaimer */}
      {showDisclaimer && (
        <div className="px-5 pb-5">
          <div className="p-4 rounded-xl bg-amber-50/80 border border-amber-200">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-amber-700 mb-1">Disclaimer</p>
                <p className="text-[11px] text-gray-600 leading-relaxed">
                  All predictions and insights are based on Vedic astrology principles and classical texts including Brihat Parashara Hora Shastra (BPHS), Jataka Parijata, and Phaladeepika. Results are for guidance only and should not replace professional medical, legal, or financial advice. Individual outcomes may vary based on free will and karmic patterns.
                </p>
              </div>
            </div>
          </div>
          
          {/* Verification Badge */}
          <div className="mt-3 flex items-center justify-center gap-4 text-[10px] text-gray-500">
            <span className="flex items-center gap-1">
              <Check className="w-3 h-3 text-[var(--palladian-dark)]" weight="bold" />
              Swiss Ephemeris Verified
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-3 h-3 text-[var(--palladian-dark)]" weight="bold" />
              Drik Ganita Compliant
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-3 h-3 text-[var(--palladian-dark)]" weight="bold" />
              Human-Verified Corpus
            </span>
          </div>
        </div>
      )}
      
      {/* Three-Layer Footer Structure */}
      <div className="border-t border-[var(--border-default)] bg-[var(--bg-muted)]">
        {/* Layer 3: Brand Statement */}
        <div className="px-5 py-4 text-center border-b border-[var(--border-default)]/50">
          <p className="text-sm text-[var(--truffle)] font-medium tracking-wide">
            AADHYAA — Rooted in Vedic Wisdom. Refined by Modern Science. Guided by Responsible AI.
          </p>
        </div>
        
        {/* Layer 2: Made in India */}
        <div className="px-5 py-3 text-center border-b border-[var(--border-default)]/50">
          <p className="text-xs text-[var(--text-secondary)] flex items-center justify-center gap-2">
            <span className="inline-block w-4 h-3 rounded-sm overflow-hidden flex-shrink-0">
              <span className="block h-1 bg-[#FF9933]"></span>
              <span className="block h-1 bg-white"></span>
              <span className="block h-1 bg-[#138808]"></span>
            </span>
            Proudly made in India for the world
          </p>
        </div>
        
        {/* Layer 1: Copyright */}
        <div className="px-5 py-3 text-center">
          <p className="text-[11px] text-[var(--text-muted)]">
            © {new Date().getFullYear()} Aadhyaa Tasteful Traditions LLP. All rights reserved.
          </p>
        </div>
      </div>
    </motion.footer>
  );
};

/**
 * VedicArchitectureCard - Detailed 4-Layer Architecture explanation
 * For use on Landing Page and educational contexts
 */
export const VedicArchitectureCard = ({ className = "" }) => {
  return (
    <motion.div
      initial={{ opacity: 0.8, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.4 }}
      className={`bg-white rounded-3xl border-2 border-amber-200 shadow-xl overflow-hidden ${className}`}
    >
      {/* Header */}
      <div className="px-8 py-6 bg-[var(--bg-muted)] border-b border-[var(--border-default)]">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-cinzel font-bold text-gray-900 mb-1">
              4-Layer Vedic Architecture
            </h3>
            <p className="text-sm text-gray-600">
              The foundation of every AADHYAA insight
            </p>
          </div>
          <div className="flex gap-1">
            {layers.map((layer) => (
              <div
                key={layer.id}
                className={`w-10 h-10 rounded-xl ${layer.color} flex items-center justify-center shadow-lg`}
              >
                <span className="text-sm font-bold text-white">{layer.id}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Layer Details */}
      <div className="p-8">
        <div className="grid md:grid-cols-2 gap-6">
          {layers.map((layer, index) => {
            const Icon = layer.icon;
            return (
              <motion.div
                key={layer.id}
                initial={{ opacity: 0.8, x: 0 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: index * 0.05 }}
                className={`p-5 rounded-2xl ${layer.bgColor} border ${layer.borderColor}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-xl ${layer.color} flex items-center justify-center shadow-lg flex-shrink-0`}>
                    <Icon className="w-7 h-7 text-white" weight="fill" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${layer.color} text-white`}>
                        {layer.id}
                      </span>
                      <h4 className={`text-lg font-cinzel font-semibold ${layer.textColor}`}>
                        {layer.name}
                      </h4>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {layer.desc}
                    </p>
                    
                    {/* Layer-specific details */}
                    <div className="mt-3 pt-3 border-t border-gray-200/50">
                      {layer.id === "L1" && (
                        <p className="text-xs text-gray-500">
                          Calculates planetary positions with sub-arcminute accuracy using the Swiss Ephemeris library
                        </p>
                      )}
                      {layer.id === "L2" && (
                        <p className="text-xs text-gray-500">
                          Applies 300+ classical Jyotish rules including Yoga calculations and Dasha periods
                        </p>
                      )}
                      {layer.id === "L3" && (
                        <p className="text-xs text-gray-500">
                          GPT-4o contextualizes interpretations while maintaining classical accuracy
                        </p>
                      )}
                      {layer.id === "L4" && (
                        <p className="text-xs text-gray-500">
                          Citations from BPHS, Jataka Parijata, Phaladeepika, and other classical texts
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
        
        {/* Bottom Stats */}
        <div className="mt-8 pt-6 border-t border-gray-200 grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-cinzel font-bold text-[var(--burning-flame)]">300+</p>
            <p className="text-xs text-gray-700">Vedic Yogas</p>
          </div>
          <div>
            <p className="text-2xl font-cinzel font-bold text-[var(--truffle)]">8</p>
            <p className="text-xs text-gray-700">Classical Texts</p>
          </div>
          <div>
            <p className="text-2xl font-cinzel font-bold text-[var(--palladian-dark)]">±1'</p>
            <p className="text-xs text-gray-700">Arc-minute Precision</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default VedicFooter;
